/**
 * Morning Kickoff 狀態管理工具
 *
 * 使用 localStorage 管理臨時狀態，支援樂觀更新和最終一致性
 */

import { httpsCallable } from 'firebase/functions';
import { functions } from '../../config/firebase';
import { logger } from '../../utils/logger';
import { formatDateToString } from '../../utils/dateUtils';

// 狀態鍵值命名規範
const DAILY_KICKOFF_KEY = (userId: string, date: string) => `daily-kickoff-${userId}-${date}`;
const WEEK_FOCUS_KEY = (userId: string, weekKey: string) => `week-focus-${userId}-${weekKey}`;
const SUGGESTION_ACCEPTED_KEY = (userId: string, date: string) => `morning-suggestions-accepted-${userId}-${date}`;

/**
 * 計算 ISO 週次 (YYYY-Www)
 */
export const getWeekKey = (date: Date): string => {
  const target = new Date(date.valueOf());
  const dayNr = (date.getDay() + 6) % 7;
  target.setDate(target.getDate() - dayNr + 3);
  const firstThursday = target.valueOf();
  target.setMonth(0, 1);
  if (target.getDay() !== 4) {
    target.setMonth(0, 1 + ((4 - target.getDay()) + 7) % 7);
  }
  const weekNumber = 1 + Math.ceil((firstThursday - target.valueOf()) / 604800000);
  
  return `${target.getFullYear()}-W${weekNumber.toString().padStart(2, '0')}`;
};

/**
 * 檢查是否在早晨時間窗口內 (6:00-11:00)
 * @param isDebugMode - 是否為除錯模式（除錯模式下全天候顯示以便測試）
 */
export const isMorningTimeWindow = (isDebugMode: boolean = false): boolean => {
  const now = new Date();
  const currentHour = now.getHours();

  // Debug 模式：全天候顯示，方便測試
  if (isDebugMode) {
    return true;
  }

  // 正常模式：嚴格的早晨時間（6:00-11:00）
  return currentHour >= 6 && currentHour < 11;
};

/**
 * 檢查是否應該顯示 Daily Kickoff
 * @param isDebugMode - 是否為除錯模式
 */
export const shouldShowDailyKickoff = (userId: string, date: string, isDebugMode: boolean = false): boolean => {
  // 檢查時間窗口
  if (!isMorningTimeWindow(isDebugMode)) {
    return false;
  }

  // 檢查是否已確認
  const isConfirmed = localStorage.getItem(DAILY_KICKOFF_KEY(userId, date)) === 'confirmed';
  return !isConfirmed;
};

/**
 * 標記 Daily Kickoff 為已確認
 * 同步到 localStorage 和後端
 */
export const confirmDailyKickoff = async (userId: string, date: string): Promise<void> => {
  // 1. 更新 localStorage（現有邏輯）
  localStorage.setItem(DAILY_KICKOFF_KEY(userId, date), 'confirmed');

  // 2. 同步到後端（新增）
  try {
    const recordConfirmation = httpsCallable(functions, 'recordMorningBriefConfirmation');
    await recordConfirmation({ date });
    logger.info('MorningKickoffState', `Morning Brief 狀態已同步到後端: ${date}`);
  } catch (error) {
    logger.error('MorningKickoffState', '後端同步失敗', error);
    // ⚠️ 不拋出錯誤，避免影響用戶體驗
  }
};

/**
 * 檢查 Daily Kickoff 是否已確認
 */
export const isDailyKickoffConfirmed = (userId: string, date: string): boolean => {
  return localStorage.getItem(DAILY_KICKOFF_KEY(userId, date)) === 'confirmed';
};

/**
 * 儲存週主題
 */
export const saveWeekFocus = (userId: string, weekKey: string, themes: string[]): void => {
  localStorage.setItem(WEEK_FOCUS_KEY(userId, weekKey), JSON.stringify({
    themes,
    setAt: new Date().toISOString()
  }));
};

/**
 * 讀取週主題
 */
export const getWeekFocus = (userId: string, weekKey: string): string[] => {
  try {
    const data = localStorage.getItem(WEEK_FOCUS_KEY(userId, weekKey));
    if (data) {
      const parsed = JSON.parse(data);
      return parsed.themes || [];
    }
  } catch (error) {
    logger.error('morningKickoffState', '讀取週主題失敗', error);
  }
  return [];
};

/**
 * 判斷應該顯示的 Kickoff 類型
 * @param userId - 用戶 ID
 * @param date - 當前時間（來自 useCurrentTime hook，每分鐘自動更新）
 * @param isDebugMode - 是否為除錯模式
 */
export const getKickoffType = (userId: string, date: Date, isDebugMode: boolean = false): 'daily' | null => {
  // 使用傳入的 date 參數（來自 useCurrentTime，確保每分鐘自動重新檢查時間窗口）
  const dateString = formatDateToString(date);
  const currentHour = date.getHours();

  // Debug 模式：全天候顯示，方便測試
  if (isDebugMode) {
    const isConfirmed = localStorage.getItem(DAILY_KICKOFF_KEY(userId, dateString)) === 'confirmed';
    return isConfirmed ? null : 'daily';
  }

  // 正常模式：6:00-11:00 且未確認時顯示
  if (currentHour >= 6 && currentHour < 11) {
    const isConfirmed = localStorage.getItem(DAILY_KICKOFF_KEY(userId, dateString)) === 'confirmed';
    return isConfirmed ? null : 'daily';
  }

  return null;
};

/**
 * 從 localStorage 讀取已接受的 suggestion keys
 */
export const getAcceptedSuggestionKeys = (userId: string, date: string): string[] => {
  try {
    const raw = localStorage.getItem(SUGGESTION_ACCEPTED_KEY(userId, date));
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
};

/**
 * 將一個 suggestion key 加入 localStorage 已接受清單
 */
export const addAcceptedSuggestionKey = (userId: string, date: string, key: string): void => {
  const current = getAcceptedSuggestionKeys(userId, date);
  if (!current.includes(key)) {
    localStorage.setItem(SUGGESTION_ACCEPTED_KEY(userId, date), JSON.stringify([...current, key]));
  }
};

/**
 * 將後端回傳的 acceptedSuggestionKeys merge 進 localStorage（跨裝置同步用）
 */
export const mergeAcceptedSuggestionKeys = (userId: string, date: string, remoteKeys: string[]): void => {
  if (remoteKeys.length === 0) return;
  const local = getAcceptedSuggestionKeys(userId, date);
  const merged = Array.from(new Set([...local, ...remoteKeys]));
  localStorage.setItem(SUGGESTION_ACCEPTED_KEY(userId, date), JSON.stringify(merged));
};

/**
 * 清除所有 Morning Kickoff 狀態 (用於測試或重置)
 */
export const clearAllMorningKickoffState = (userId: string): void => {
  // 清除每日狀態
  const today = new Date();
  // 使用本地時間格式化日期，避免 UTC 時區轉換問題
  const dateString = formatDateToString(today);
  localStorage.removeItem(DAILY_KICKOFF_KEY(userId, dateString));

  // 清除週主題（保留此功能，未來可能有用）
  const weekKey = getWeekKey(today);
  localStorage.removeItem(WEEK_FOCUS_KEY(userId, weekKey));

  // 清除過去一週的狀態 (可選，用於清理)
  for (let i = 0; i < 7; i++) {
    const pastDate = new Date(today);
    pastDate.setDate(today.getDate() - i);
    // 使用本地時間格式化日期，避免 UTC 時區轉換問題
    const pastDateString = formatDateToString(pastDate);
    localStorage.removeItem(DAILY_KICKOFF_KEY(userId, pastDateString));
  }
};

/**
 * 獲取當前週的主題（如果有的話）
 */
export const getCurrentWeekFocus = (userId: string): string[] => {
  const today = new Date();
  const weekKey = getWeekKey(today);
  return getWeekFocus(userId, weekKey);
};

/**
 * 防重複請求工具
 */
export const createRequestDebouncer = () => {
  let lastRequestTime = 0;
  const DEBOUNCE_TIME = 5000; // 5秒
  
  return {
    shouldProceed: () => {
      const now = Date.now();
      if (now - lastRequestTime < DEBOUNCE_TIME) {
        return false;
      }
      lastRequestTime = now;
      return true;
    },
    reset: () => {
      lastRequestTime = 0;
    }
  };
};
